import React, { useRef } from 'react';
import faceOutlineGuide from '../../../assets/images/face-outline-guide.svg';
import Wrap from '../../Wrap';
import { Button, ButtonStack } from '../../library/Button';
import { Divider } from '../../library/Divider';
import { SectionContainerWrapper } from '../../common/GlobalElements/Wrappers';
import PhotoRequirements from './PhotoRequirements';
import {
    OptionsColumn,
    OptionsGrid,
    OptionsHeading,
    OptionsHint,
    QrCode,
    QrFrame,
    QrOverlay,
    RefreshQrLink,
} from './ProfilePictureUpload.styles';

/**
 * Rendered edge length of the QR code, matching the box the hosted QR image used to occupy.
 */
const QR_SIZE_PX = 160;

/**
 * Quiet zone around the QR code, in QR modules. The spec asks for 4, but the code sits on
 * the dialog's white background, so 2 is enough to keep the modules off the SVG edge.
 */
const QR_QUIET_ZONE_MODULES = 2;

/**
 * Landing screen of the enrollment dialog: photo requirements on the left,
 * and on the right either a "scan with your phone" QR code (desktop) or
 * a plain "Upload"/"Take Photo" choice (mobile, where there's no point
 * scanning a QR code to reach the same device).
 */
function PhotoUploadOptions({
    isMobile,
    mobileUploadUrl,
    localDebugUrl,
    isPhotoIncoming,
    isRefreshingQr,
    onFileSelect,
    onTakePhoto,
    onDeletePhoto,
    onRefreshQr,
}) {
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        const inputEl = event.target;
        const file = inputEl.files?.[0];
        inputEl.value = '';

        if (file) {
            onFileSelect(file);
        }
    };

    return (
        <OptionsGrid $isMobile={isMobile}>
            <SectionContainerWrapper flex={7}>
                <PhotoRequirements />
            </SectionContainerWrapper>

            <SectionContainerWrapper flex={3}>
                <OptionsColumn>
                    {isMobile ? (
                        <img
                            src={faceOutlineGuide}
                            alt="Headshot placeholder"
                            style={{ width: '100%', maxWidth: '240px' }}
                        />
                    ) : (
                        <>
                            <OptionsHeading>Scan to Continue on Mobile</OptionsHeading>
                            {mobileUploadUrl ? (
                                <QrFrame>
                                    <Wrap
                                        if={!!localDebugUrl && !isPhotoIncoming}
                                        wrapper={(child) => (
                                            <a href={localDebugUrl} target="_blank" rel="noopener noreferrer">
                                                {child}
                                            </a>
                                        )}
                                    >
                                        <QrCode
                                            value={mobileUploadUrl}
                                            size={QR_SIZE_PX}
                                            marginSize={QR_QUIET_ZONE_MODULES}
                                            title="Scan to upload from your phone"
                                            $isDimmed={isPhotoIncoming}
                                        />
                                    </Wrap>
                                    {isPhotoIncoming && (
                                        <QrOverlay>
                                            <i className="fas fa-cog fa-spin"></i>
                                        </QrOverlay>
                                    )}
                                </QrFrame>
                            ) : (
                                <OptionsHint>Connecting...</OptionsHint>
                            )}

                            <RefreshQrLink
                                type="button"
                                onClick={onRefreshQr}
                                disabled={isRefreshingQr || isPhotoIncoming}
                            >
                                {isRefreshingQr ? 'Refreshing…' : 'QR code not working? Refresh it'}
                            </RefreshQrLink>

                            <Divider>or</Divider>
                        </>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <ButtonStack>
                        <Button
                            $variant="secondary"
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isPhotoIncoming}
                        >
                            <i className="fal fa-upload"></i> Upload Photo
                        </Button>
                        <Button type="button" onClick={onTakePhoto} disabled={isPhotoIncoming}>
                            <i className="fal fa-camera"></i> Take Photo
                        </Button>
                        {onDeletePhoto ? (
                            <Button $variant="alert" type="button" onClick={onDeletePhoto} disabled={isPhotoIncoming}>
                                <i className="fal fa-trash-alt"></i> Delete Photo
                            </Button>
                        ) : null}
                    </ButtonStack>
                </OptionsColumn>
            </SectionContainerWrapper>
        </OptionsGrid>
    );
}

export default PhotoUploadOptions;
